/**
 * UploadModule — the Creator UGC upload panel (Graphics G6).
 *
 * A self-contained floating panel: pick a .glb → upload the raw bytes to the store →
 * `beginUpload` → live-poll the asset_upload status (validating / transcoding / ready /
 * rejected + reason) → on ready, Place it in the current world (`rezUploadedPart`) or Submit
 * it for moderation. The client RENDERS + CALLS reducers; validation / transcode / provenance
 * are all server + worker side. (The worker picks up the raw upload and ingests it.)
 */
import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { ASSET_STORE_BASE } from '../config.js';
import { iconMarkup } from '../lib/icons.js';
import { groundYIn } from '../lib/grounding.js';

const USE_CLASSES = ['mesh', 'decor', 'prop', 'structure', 'foliage'];

export class UploadModule implements IModule {
  readonly name = 'Upload';
  private panel!: HTMLElement;
  private list!: HTMLElement;
  private bound = false;

  init(_app: App): void {
    this.buildPanel();
    bus.on('atlas:account-ready', () => { this.subscribe(); this.render(); });
    bus.on('atlas:upload-updated', () => this.render());
  }

  private subscribe(): void {
    if (this.bound) return;
    const conn = atlas.conn;
    if (!conn) return;
    this.bound = true;
    conn.subscriptionBuilder().subscribe(['SELECT * FROM asset_upload', 'SELECT * FROM moderation_queue', 'SELECT * FROM render_asset']);
    const ping = (): void => bus.emit('atlas:upload-updated', {});
    for (const tbl of [conn.db.assetUpload, conn.db.moderationQueue]) {
      const t = tbl as unknown as { onInsert(cb: () => void): void; onUpdate(cb: () => void): void; onDelete(cb: () => void): void };
      t.onInsert(ping); t.onUpdate(ping); t.onDelete(ping);
    }
  }

  private buildPanel(): void {
    this.panel = document.createElement('div');
    this.panel.id = 'ugc-upload';
    // Hidden by default — it rendered PERMANENTLY on the right edge for every
    // player (the HUD-clutter report, 2026-07-30). F7 / the menu bar toggles.
    this.panel.style.cssText = 'position:fixed;right:12px;bottom:64px;width:300px;background:#0d1117ee;border:1px solid #30363d;border-radius:8px;padding:10px;font:12px system-ui;color:#c9d1d9;z-index:50;display:none';
    window.addEventListener('keydown', (e) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (e.code === 'F7') {
        e.preventDefault();
        this.panel!.style.display = this.panel!.style.display === 'none' ? 'block' : 'none';
      }
    });
    this.panel.innerHTML =
      `<div style="font-weight:600;margin-bottom:6px">${iconMarkup('box')}Creator Uploads</div>` +
      `<label style="display:block;margin-bottom:6px">Class <select id="ugc-class">${USE_CLASSES.map((c) => `<option>${c}</option>`).join('')}</select>` +
      ' <input id="ugc-file" type="file" accept=".glb" style="width:150px"></label>' +
      '<div id="ugc-list"></div>';
    document.body.appendChild(this.panel);
    this.list = this.panel.querySelector('#ugc-list')!;
    this.panel.querySelector('#ugc-file')!.addEventListener('change', (e) => void this.onFile(e));
  }

  private async onFile(e: Event): Promise<void> {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    const conn = atlas.conn;
    if (!file || !conn) return;
    const useClass = (this.panel.querySelector('#ugc-class') as HTMLSelectElement).value;
    const rawKey = `${atlas.identityHex ?? 'anon'}-${file.name}`.replace(/\s+/g, '_');
    // Raw uploads land in the writable uploads bucket (the cooked-asset bucket stays read-only).
    const uploadBase = ASSET_STORE_BASE.replace(/\/[^/]+$/, '/atlas-uploads');
    try {
      await fetch(`${uploadBase}/${rawKey}`, { method: 'PUT', body: await file.arrayBuffer(), headers: { 'Content-Type': 'model/gltf-binary' } });
    } catch { /* prod uses a presigned URL; surface upload state via the asset_upload status either way */ }
    void conn.reducers
      .beginUpload({ originalFilename: file.name, rawUri: rawKey, useClass, worldId: atlas.currentWorldId ?? 0n })
      .catch((err: Error) => { const d = document.getElementById('ugc-list'); if (d) d.textContent = `Upload failed: ${err.message}`; });
    input.value = '';
  }

  private render(): void {
    const conn = atlas.conn;
    if (!conn || !atlas.identityHex) { this.list.innerHTML = ''; return; }
    const mine = [...conn.db.assetUpload.iter()]
      .filter((u) => u.uploader.toHexString() === atlas.identityHex)
      .sort((a, b) => Number(b.uploadId - a.uploadId))
      .slice(0, 6);
    if (mine.length === 0) { this.list.innerHTML = '<div style="opacity:.6">No uploads yet — pick a .glb above.</div>'; return; }
    this.list.innerHTML = '';
    for (const u of mine) {
      const mod = u.assetId > 0n ? conn.db.moderationQueue.assetId.find(u.assetId) : undefined;
      const colour = u.status === 'ready' ? '#3fb950' : u.status === 'rejected' ? '#f85149' : '#d29922';
      const row = document.createElement('div');
      row.style.cssText = 'border-top:1px solid #21262d;padding:6px 0';
      // User-controlled strings (filename, rejectReason) go through textContent — never innerHTML (XSS).
      const head = document.createElement('div');
      const name = document.createElement('b'); name.textContent = u.originalFilename; head.appendChild(name);
      head.appendChild(document.createTextNode(' — '));
      const st = document.createElement('span'); st.style.color = colour; st.textContent = u.status; head.appendChild(st);
      if (u.status === 'rejected') { const r = document.createElement('span'); r.style.opacity = '.8'; r.textContent = ` (${u.rejectReason})`; head.appendChild(r); }
      if (mod) head.appendChild(document.createTextNode(` · mod: ${mod.state}`));
      row.appendChild(head);
      if (u.status === 'ready') {
        const place = document.createElement('button'); place.textContent = 'Place';
        place.addEventListener('click', () => this.place(u.assetId));
        row.appendChild(place);
        if (!mod) {
          const submit = document.createElement('button'); submit.textContent = 'Submit for moderation'; submit.style.marginLeft = '4px';
          submit.addEventListener('click', () => {
            void conn.reducers
              .submitForModeration({ assetId: u.assetId })
              .catch((err: Error) => { const d = document.getElementById('ugc-list'); if (d) d.textContent = `Submit failed: ${err.message}`; });
          });
          row.appendChild(submit);
        }
      }
      this.list.appendChild(row);
    }
  }

  private place(renderAssetId: bigint): void {
    const conn = atlas.conn;
    if (!conn || atlas.currentWorldId === null) return;
    void conn.reducers
      .rezUploadedPart({
        worldId: atlas.currentWorldId, renderAssetId,
        // Ground-seated (high-relief audit 2026-08-05): posY 0 sank the part
        // (and its physics collider) below any terrain that isn't at sea level.
        posX: atlas.localX, posY: groundYIn(conn, atlas.currentWorldId, atlas.localX, atlas.localZ) + 0.5, posZ: atlas.localZ,
        rotX: 0, rotY: 0, rotZ: 0, rotW: 1,
        scaleX: 1, scaleY: 1, scaleZ: 1, physics: true,
      })
      .catch((err: Error) => { const d = document.getElementById('ugc-list'); if (d) d.textContent = `Place failed: ${err.message}`; });
  }
}
